import { tasbeehOptions } from '../data/dhikrCatalog';
import { reachedTasbeehGoals, tasbeehGoalReachedError } from './tasbeehGoals';

export const OFFLINE_TASBEEH_CHANGED = 'amposter:offline-tasbeeh-changed';
export const OFFLINE_TASBEEH_SYNCED = 'amposter:offline-tasbeeh-synced';
const MAX = 1000000000000;
const valid = value => Number.isSafeInteger(value) && value > 0 ? Math.min(value, MAX) : 0;
let opening;

function open() {
  if (!opening) opening = new Promise((resolve, reject) => {
    const request = indexedDB.open('amposter-offline-tasbeeh', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('accounts', { keyPath: 'accountId' });
    request.onsuccess = () => {
      request.result.onversionchange = () => { request.result.close(); opening = null; };
      resolve(request.result);
    };
    request.onerror = () => { opening = null; reject(request.error); };
  }).catch(error => { opening = null; throw error; });
  return opening;
}

function legacyCounts(accountId) {
  try {
    const saved = JSON.parse(localStorage.getItem(`amposter:offline-dhikr:v1:${accountId}:tasbeeh`));
    const counts = saved && typeof saved === 'object' && !Array.isArray(saved) ? saved
      : { 4: Number(localStorage.getItem(`amposter:offline-tasbeeh:v1:${accountId}`)) };
    return Object.fromEntries(tasbeehOptions.map((dhikr,id) => [dhikr, { total: valid(counts[id]), acknowledged: 0 }]));
  } catch { return {}; }
}

async function update(accountId, mutate, create = false) {
  if (!accountId || accountId === 'guest') return null;
  // Snapshot before yielding: the visible counter may change while IndexedDB opens.
  const legacy = legacyCounts(accountId);
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('accounts', 'readwrite'), store = tx.objectStore('accounts');
    const request = store.get(accountId);
    let result;
    request.onsuccess = () => {
      let row = request.result;
      if (!row && (create || Object.values(legacy).some(entry => entry.total > 0))) {
        row = { accountId, deviceId: crypto.randomUUID(), counts: legacy };
      }
      if (row) { mutate(row); store.put(row); }
      result = row || null;
    };
    tx.oncomplete = () => resolve(result);
    tx.onerror = tx.onabort = () => reject(tx.error || new Error('Offline storage unavailable'));
  });
}

export const readOfflineTasbeeh = accountId => update(accountId, () => {});

export function pendingOfflineDay(row, day = new Date().toISOString().slice(0,10), dhikr, serverAccepted) {
  // When combined with a server snapshot, use that snapshot's acknowledgement.
  // A newer local HTTP acknowledgement must not temporarily hide pending taps.
  return Object.values(row?.dailyCounts || {}).filter(entry => entry.activityDate === day && (!dhikr || entry.dhikr === dhikr))
    .reduce((sum,entry) => sum + Math.max(0,valid(entry.total)-valid(serverAccepted ? serverAccepted[`${row.deviceId}:${entry.dhikr}`] : entry.acknowledged)),0);
}

export async function recordOfflineTasbeeh(accountId, dhikr, { baseView, enforceGoals = false } = {}) {
  if (!tasbeehOptions.includes(dhikr)) throw new Error('Unknown dhikr');
  // Capture the activity day before awaiting storage, using the same UTC day as the server.
  const day = new Date().toISOString().slice(0, 10);
  let blocked = [];
  const row = await update(accountId, record => {
    if (enforceGoals && baseView) {
      blocked = reachedTasbeehGoals(accountId,dhikr,{...baseView,todayCount:baseView.todayCount+pendingOfflineDay(record,day,undefined,baseView.offlineDailyAccepted),dhikrTodayCount:baseView.dhikrTodayCount+pendingOfflineDay(record,day,dhikr,baseView.offlineDailyAccepted)},day);
      if (blocked.length) return;
    }
    const previous = record.counts[dhikr] || { total: 0, acknowledged: 0 };
    if (previous.total >= MAX) return;
    record.counts[dhikr] = { ...previous, total: Math.min(MAX, valid(previous.total) + 1) };
    record.dailyCounts ||= {};
    const key = JSON.stringify([day, dhikr]);
    const daily = record.dailyCounts[key] || { dhikr, activityDate: day, total: 0, acknowledged: 0 };
    record.dailyCounts[key] = { ...daily, total: valid(daily.total) + 1 };
  }, true);
  if (blocked.length) throw tasbeehGoalReachedError(blocked);
  if (row) window.dispatchEvent(new Event(OFFLINE_TASBEEH_CHANGED));
  return row;
}

export function pendingTasbeeh(row) {
  if (!row) return null;
  const dailyCounts = Object.values(row.dailyCounts || {}).filter(entry => entry.total > entry.acknowledged)
    .sort((a,b) => a.activityDate.localeCompare(b.activityDate) || a.dhikr.localeCompare(b.dhikr)).slice(0,366)
    .map(({ dhikr, activityDate, total }) => ({ dhikr, activityDate, totalCount: total }));
  const datedDhikr = new Set(dailyCounts.map(entry => entry.dhikr));
  const counts = Object.entries(row.counts).filter(([dhikr, entry]) => entry.total > entry.acknowledged || datedDhikr.has(dhikr))
    .map(([dhikr, entry]) => ({ dhikr, totalCount: entry.total }));
  return counts.length ? { accountId: row.accountId, deviceId: row.deviceId, counts, ...(dailyCounts.length ? { dailyCounts } : {}) } : null;
}

export async function acknowledgeOfflineTasbeeh(snapshot) {
  await update(snapshot.accountId, row => {
    if (row.deviceId !== snapshot.deviceId) return;
    snapshot.counts.forEach(({ dhikr, totalCount }) => {
      const entry = row.counts[dhikr];
      if (entry) entry.acknowledged = Math.max(entry.acknowledged, Math.min(totalCount, entry.total));
    });
    (snapshot.dailyCounts || []).forEach(({ dhikr, activityDate, totalCount }) => {
      const entry = row.dailyCounts?.[JSON.stringify([activityDate,dhikr])];
      if (entry) entry.acknowledged = Math.max(entry.acknowledged, Math.min(totalCount, entry.total));
    });
  });
}
